import { normalizeLang } from "./normalizeLang";

const DEFAULT_LOCALE = "en-US";
const DEFAULT_CURRENCY_META = {
  VND: { symbol: "₫", decimal_places: 0, intl_code: "VND" },
  USD: { symbol: "$", decimal_places: 2, intl_code: "USD" },
  JPY: { symbol: "¥", decimal_places: 0, intl_code: "JPY" },
  KOR: { symbol: "₩", decimal_places: 0, intl_code: "KRW" },
  KRW: { symbol: "₩", decimal_places: 0, intl_code: "KRW" },
};
const CURRENCY_META_STORAGE_KEY = "currency_meta_cache";
let currencyMetaCache = null;
let localeSeparatorCache = {};

function isNil(value) {
  return value === null || value === undefined;
}

export function toFiniteNumber(value) {
  if (isNil(value) || value === "") return null;
  const num = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  return Number.isFinite(num) ? num : null;
}

export function formatNumberWithCommas(
  value,
  {
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    locale = DEFAULT_LOCALE,
  } = {}
) {
  const num = toFiniteNumber(value);
  if (num === null) return "";
  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

export function formatPlainNumber(
  value,
  { minimumFractionDigits = 0, maximumFractionDigits = 2, locale } = {}
) {
  if (value === null || value === undefined || value === "") return "-";
  const resolvedLocale =
    locale ||
    resolveLocaleFromLang(
      typeof localStorage === "undefined" ? null : localStorage.getItem("lang")
    );
  const num = toFiniteNumber(value);
  if (num === null) return "-";
  return new Intl.NumberFormat(resolvedLocale, {
    useGrouping: true,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

export function formatCurrencyVND(value, { unit = "₫", withSpace = true } = {}) {
  const formatted = formatNumberWithCommas(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (!formatted) return "";
  if (!unit) return formatted;
  return withSpace ? `${formatted} ${unit}` : `${formatted}${unit}`;
}

export function resolveLocaleFromLang(lang) {
  const normalized = normalizeLang(lang);
  if (normalized === "vi") return "vi-VN";
  if (normalized === "ja") return "ja-JP";
  return DEFAULT_LOCALE;
}

export function resolveCurrencyDecimals(currencyCode, { currencyMeta = {} } = {}) {
  const code = String(currencyCode || "VND").toUpperCase()
  const storedMeta = loadCurrencyMeta()
  const meta = currencyMeta?.[code] || storedMeta?.[code] || DEFAULT_CURRENCY_META[code] || {}
  const decimals = Number.isFinite(meta.decimal_places)
    ? Number(meta.decimal_places)
    : DEFAULT_CURRENCY_META[code]?.decimal_places ?? 2
  return decimals
}

export function buildCurrencyMetaMap(list = []) {
  if (!Array.isArray(list)) return {};
  return list.reduce((acc, currency) => {
    if (!currency?.code) return acc;
    acc[String(currency.code).toUpperCase()] = {
      symbol: currency.symbol,
      decimal_places: Number.isFinite(currency.decimal_places)
        ? Number(currency.decimal_places)
        : undefined,
    };
    return acc;
  }, {});
}

export function saveCurrencyMeta(list = []) {
  const meta = buildCurrencyMetaMap(list);
  currencyMetaCache = meta;
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(CURRENCY_META_STORAGE_KEY, JSON.stringify(meta));
    } catch (error) {
      // Ignore storage errors (e.g. quota exceeded)
    }
  }
  return meta;
}

export function loadCurrencyMeta() {
  if (currencyMetaCache) return currencyMetaCache;
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(CURRENCY_META_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      currencyMetaCache = parsed;
      return parsed;
    }
  } catch (error) {
    return {};
  }
  return {};
}

export function formatCurrencyByCode(
  value,
  { currencyCode = "VND", currencyMeta = {}, locale } = {}
) {
  if (value === null || value === undefined || value === "") return "-";
  const code = String(currencyCode || "VND").toUpperCase();
  const storedMeta = loadCurrencyMeta();
  const meta = currencyMeta?.[code] || storedMeta?.[code] || DEFAULT_CURRENCY_META[code] || {};
  const decimals = resolveCurrencyDecimals(code, { currencyMeta })
  const resolvedLocale =
    locale ||
    resolveLocaleFromLang(
      typeof localStorage === "undefined" ? null : localStorage.getItem("lang")
    );
  const intlCode = meta.intl_code || DEFAULT_CURRENCY_META[code]?.intl_code || code;

  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency: intlCode,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(Number(value));
  } catch (error) {
    const formatted = formatNumberWithCommas(value, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      locale: resolvedLocale,
    });
    const symbol = meta.symbol || DEFAULT_CURRENCY_META[code]?.symbol;
    if (symbol) return `${formatted} ${symbol}`;
    return `${formatted} ${code}`.trim();
  }
}

export function resolveLocaleNumberSeparators(locale = DEFAULT_LOCALE) {
  const resolvedLocale = locale || DEFAULT_LOCALE

  if (localeSeparatorCache[resolvedLocale]) return localeSeparatorCache[resolvedLocale]

  try {
    const parts = new Intl.NumberFormat(resolvedLocale).formatToParts(1000.1)
    let group = ","
    let decimal = "."
    parts.forEach((part) => {
      if (part.type === "group") group = part.value
      if (part.type === "decimal") decimal = part.value
    })

    const out = { group, decimal }
    localeSeparatorCache[resolvedLocale] = out
    return out
  } catch (error) {
    const out = { group: ",", decimal: "." }
    localeSeparatorCache[resolvedLocale] = out
    return out
  }
}

function shouldTreatSingleSeparatorAsGrouping({ separatorIndex, cleaned, separator }) {
  const digitsAfter = cleaned.slice(separatorIndex + 1).replace(/\D/g, "");
  const digitsBefore = cleaned.slice(0, separatorIndex).replace(/\D/g, "");

  if (digitsAfter.length === 3 && digitsBefore.length > 0) return true;
  if (separator === "," && digitsAfter.length === 3 && digitsBefore.length >= 2) return true;
  if (separator === "." && digitsAfter.length === 3 && digitsBefore.length >= 2) return true;
  return false;
}

export function normalizeNumericString(
  input,
  {
    allowDecimal = true,
    allowNegative = false,
    decimalScale,
    preserveTrailingDecimalSeparator = false,
  } = {}
) {
  if (isNil(input)) return "";
  const raw = String(input).trim();
  if (!raw) return "";

  const hasNegative = allowNegative && raw.startsWith("-");
  const sign = hasNegative ? "-" : "";

  const cleaned = raw.replace(/[^\d.,]/g, "");
  if (!cleaned) return sign ? "-" : "";

  if (!allowDecimal) {
    const digits = cleaned.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    return sign + digits;
  }

  const commas = (cleaned.match(/,/g) || []).length;
  const dots = (cleaned.match(/\./g) || []).length;

  let decimalSeparator = null;
  if (commas > 0 && dots > 0) {
    decimalSeparator = cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".") ? "," : ".";
  } else if (commas > 0 && dots === 0) {
    if (commas === 1) {
      const idx = cleaned.lastIndexOf(",");
      decimalSeparator = shouldTreatSingleSeparatorAsGrouping({
        separatorIndex: idx,
        cleaned,
        separator: ",",
      })
        ? null
        : ",";
    }
  } else if (dots > 0 && commas === 0) {
    if (dots === 1) {
      const idx = cleaned.lastIndexOf(".");
      decimalSeparator = shouldTreatSingleSeparatorAsGrouping({
        separatorIndex: idx,
        cleaned,
        separator: ".",
      })
        ? null
        : ".";
    }
  }

  if (!decimalSeparator) {
    const digits = cleaned.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
    return sign + digits;
  }

  const splitIndex = cleaned.lastIndexOf(decimalSeparator);
  const integerPart = cleaned.slice(0, splitIndex).replace(/\D/g, "");
  let fractionPart = cleaned.slice(splitIndex + 1).replace(/\D/g, "");

  if (typeof decimalScale === "number" && decimalScale >= 0) {
    fractionPart = fractionPart.slice(0, decimalScale);
  }

  const normalizedInteger = (integerPart || "0").replace(/^0+(?=\d)/, "") || "0";
  if (!fractionPart) {
    if (preserveTrailingDecimalSeparator && cleaned.endsWith(decimalSeparator)) {
      return `${sign}${normalizedInteger}.`;
    }
    return sign + normalizedInteger;
  }
  return `${sign}${normalizedInteger}.${fractionPart}`;
}

// Normalizes decimal strings for API payloads (backend expects "." as decimal separator).
// This is intentionally more permissive than `normalizeNumericString` for cases like "0,005".
export function normalizeDecimalForApi(input) {
  if (input === null || input === undefined) return "";
  const raw = String(input).trim();
  if (!raw) return "";

  let value = raw.replace(/\s+/g, "");
  const hasComma = value.includes(",");
  const hasDot = value.includes(".");

  if (hasComma && hasDot) {
    const lastComma = value.lastIndexOf(",");
    const lastDot = value.lastIndexOf(".");
    const decimalSep = lastComma > lastDot ? "," : ".";
    const groupingSep = decimalSep === "," ? "." : ",";
    value = value.split(groupingSep).join("");
    if (decimalSep === ",") value = value.replace(",", ".");
  } else if (hasComma && !hasDot) {
    value = value.replace(",", ".");
  }

  value = value.replace(/[^0-9.]/g, "");

  const parts = value.split(".");
  if (parts.length > 2) {
    value = `${parts[0]}.${parts.slice(1).join("")}`;
  }

  return value;
}

export function formatNumericStringDisplay(
  normalized,
  { format = "number", decimalScale, allowNegative = false } = {}
) {
  if (isNil(normalized) || normalized === "" || normalized === "-") return "";
  const allowDecimal = format !== "currency";
  const normalizedValue = normalizeNumericString(normalized, {
    allowDecimal,
    allowNegative,
    decimalScale,
  });
  if (!normalizedValue) return "";

  const [integerPartRaw, fractionPartRaw] = normalizedValue.split(".");
  const integerFormatted = formatNumberWithCommas(integerPartRaw, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (!allowDecimal || !fractionPartRaw) return integerFormatted;
  return `${integerFormatted}.${fractionPartRaw}`;
}

export function formatNumericStringDisplayLocalized(
  normalized,
  {
    format = "number",
    decimalScale,
    locale,
    allowNegative = false,
    preserveTrailingDecimalSeparator = false,
  } = {}
) {
  if (isNil(normalized) || normalized === "" || normalized === "-") return ""

  const resolvedLocale =
    locale ||
    resolveLocaleFromLang(
      typeof localStorage === "undefined" ? null : localStorage.getItem("lang")
    )

  const allowDecimal = format !== "currency" || (typeof decimalScale === "number" && decimalScale > 0)
  const normalizedValue = normalizeNumericString(normalized, {
    allowDecimal,
    allowNegative,
    decimalScale,
    preserveTrailingDecimalSeparator,
  })
  if (!normalizedValue) return ""

  const { decimal: decimalSeparator } = resolveLocaleNumberSeparators(resolvedLocale)

  const hasTrailingDecimal = allowDecimal && normalizedValue.endsWith(".")
  const [integerPartWithSign, fractionPartRaw] = normalizedValue.split(".")
  const sign = integerPartWithSign.startsWith("-") ? "-" : ""
  const integerPartRaw = integerPartWithSign.replace(/^-/, "")

  const integerFormatted = new Intl.NumberFormat(resolvedLocale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(integerPartRaw || 0))

  if (!allowDecimal) return `${sign}${integerFormatted}`

  if (hasTrailingDecimal && !fractionPartRaw) {
    return `${sign}${integerFormatted}${decimalSeparator}`
  }

  if (!fractionPartRaw) return `${sign}${integerFormatted}`

  return `${sign}${integerFormatted}${decimalSeparator}${fractionPartRaw}`
}
